import { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { faBars } from '@fortawesome/pro-regular-svg-icons/faBars';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import Icon from '@veeva/corgix/icon';
import { Menu, MenuHeader, MenuItem } from '@veeva/corgix/menu';
import Overlay from '@veeva/corgix/overlay';
import NavigationTabs from '../../constants/NavigationTabs';
import { AuthenticatedPaths } from '../../constants/Paths';
import mobileContainerHeightCSS from '../../constants/mobileContainerHeightCSS';
import veevaIdLogo from '../../images/veeva-id-logo.svg';
import useMessageService from '../../services/i18n/useMessageService';
import logout from '../../services/logout';
import { useUserSessionContext } from '../UserSessionContextWrapper';
import getTargetAttribute from '../../services/getTargetAttribute';

const HamburgerNavigation = ({ headerContent, selectedTab }) => {
    const history = useHistory();
    const [open, setOpen] = useState(false);
    const [accountSubmenuOpen, setAccountSubmenuOpen] = useState(true);
    const { showStudiesTab } = useUserSessionContext();

    const [
        [
            systemsLabel,
            myAccountLabel,
            generalLabel,
            emailsLabel,
            changePasswordLabel,
            recentActivityLabel,
            myStudiesLabel,
            studiesLabel,
            logoutLabel,
            veevaIdLogoAlt,
        ],
    ] = useMessageService([
        'home.systems',
        'my_account.my_account',
        'my_account.general',
        'my_account.emails',
        'my_account.change_password',
        'my_account.recent_activity',
        'home.my_studies',
        'home.studies',
        'general.logout',
        'branded_logo.veeva_id_logo_alt',
    ]);

    const currentPageTitle = {
        [NavigationTabs.SYSTEMS]: systemsLabel,
        [NavigationTabs.CHANGE_PASSWORD]: changePasswordLabel,
        [NavigationTabs.EMAILS]: emailsLabel,
        [NavigationTabs.GENERAL]: generalLabel,
        [NavigationTabs.LOGOUT]: logoutLabel,
        [NavigationTabs.STUDIES]: myStudiesLabel,
        [NavigationTabs.RECENT_ACTIVITY]: recentActivityLabel,
    }[selectedTab];

    const pageTitleDataTarget = {
        [NavigationTabs.STUDIES]: 'my-studies-title',
    }[selectedTab];

    const handleHamburgerClick = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleTabSelect = (e, tab) => {
        switch (tab) {
            case NavigationTabs.SYSTEMS:
                setOpen(false);
                history.push(AuthenticatedPaths.SYSTEMS);
                break;
            case NavigationTabs.STUDIES:
                setOpen(false);
                history.push(AuthenticatedPaths.STUDIES);
                break;
            case NavigationTabs.GENERAL:
                setOpen(false);
                history.push(AuthenticatedPaths.MY_ACCOUNT);
                break;
            case NavigationTabs.EMAILS:
                setOpen(false);
                history.push(AuthenticatedPaths.EMAILS);
                break;
            case NavigationTabs.CHANGE_PASSWORD:
                setOpen(false);
                history.push(AuthenticatedPaths.CHANGE_PASSWORD);
                break;
            case NavigationTabs.RECENT_ACTIVITY:
                setOpen(false);
                history.push(AuthenticatedPaths.RECENT_ACTIVITY);
                break;
            case NavigationTabs.LOGOUT:
                logout();
                break;
            case 'EXPAND_ACCOUNT':
                setAccountSubmenuOpen(!accountSubmenuOpen);
                break;
            default:
                break;
        }
    };

    const menuHeaderCSS = css`
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        border-bottom: 0;
        padding: 24px 48px;

        & > img {
            height: 2rem;
        }
    `;

    const menuItemCSS = css`
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        padding: 24px 48px;
    `;

    const topLevelMenuItemCSS = ({ silverDefault }) => [
        menuItemCSS,
        css`
            border-top: 1px solid ${silverDefault};
            font-size: 20px;
        `,
    ];

    const expandableMenuItemCSS = ({ black, silverDefault }) => [
        topLevelMenuItemCSS({ silverDefault }),
        css`
            text-transform: none;
            color: ${black};
            font-weight: bold;
            border-top: 1px solid ${silverDefault};
            ${accountSubmenuOpen
                ? `border-bottom: 1px solid ${silverDefault};`
                : 'border-bottom: 0'}
        `,
    ];

    const submenuItemCSS = [
        menuItemCSS,
        css`
            font-size: 18px;
        `,
    ];

    const overlay = (
        <Overlay
            open
            css={[
                css`
                    top: 0;
                    transition: all 0.2s ease-in-out;
                    -webkit-tap-highlight-color: transparent; // Fix for Chrome mobile devices blue highlight on tap
                `,
                !open &&
                    css`
                        transform: translateX(-100vw);
                        opacity: 0;
                    `,
            ]}
        >
            <Menu
                css={({ black, white }) => css`
                    display: flex;
                    flex-direction: column;
                    width: 100vw;
                    background-color: ${white};
                    box-shadow: none;
                    max-height: 100vh;
                    overflow-y: scroll;
                    ${mobileContainerHeightCSS}

                    svg {
                        color: ${black};
                    }
                `}
                onClick={handleTabSelect}
            >
                <MenuHeader css={menuHeaderCSS}>
                    <Icon
                        type={faTimes}
                        size="lg"
                        css={css`
                            position: absolute;
                            left: 24px;
                        `}
                        onClick={handleClose}
                    />
                    <img src={veevaIdLogo} alt={veevaIdLogoAlt} />
                </MenuHeader>
                {showStudiesTab && (
                    <MenuItem value={NavigationTabs.STUDIES} css={topLevelMenuItemCSS}>
                        {studiesLabel}
                    </MenuItem>
                )}
                <MenuItem value={NavigationTabs.SYSTEMS} css={topLevelMenuItemCSS}>
                    {systemsLabel}
                </MenuItem>
                <MenuItem value="EXPAND_ACCOUNT" css={expandableMenuItemCSS}>
                    {myAccountLabel}
                    <Icon
                        type={accountSubmenuOpen ? faChevronDown : faChevronRight}
                        size="md"
                        css={css`
                            position: absolute;
                            right: 24px;
                        `}
                    />
                </MenuItem>
                {accountSubmenuOpen && (
                    <MenuItem value={NavigationTabs.GENERAL} css={submenuItemCSS}>
                        {generalLabel}
                    </MenuItem>
                )}
                {accountSubmenuOpen && (
                    <MenuItem value={NavigationTabs.EMAILS} css={submenuItemCSS}>
                        {emailsLabel}
                    </MenuItem>
                )}
                {accountSubmenuOpen && (
                    <MenuItem value={NavigationTabs.CHANGE_PASSWORD} css={submenuItemCSS}>
                        {changePasswordLabel}
                    </MenuItem>
                )}
                {accountSubmenuOpen && (
                    <MenuItem value={NavigationTabs.RECENT_ACTIVITY} css={submenuItemCSS}>
                        {recentActivityLabel}
                    </MenuItem>
                )}
                <MenuItem value={NavigationTabs.LOGOUT} css={topLevelMenuItemCSS}>
                    {logoutLabel}
                </MenuItem>
            </Menu>
        </Overlay>
    );

    return (
        <>
            {overlay}
            <header
                css={({ white, silverDefault }) => css`
                    font-size: 20px;
                    font-weight: bold;
                    padding: 24px 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    position: sticky;
                    top: 0;
                    z-index: 11; // Ensures header z-index is greater than DataGrid's header z-index of 10
                    background-color: ${white};
                    height: 2rem;
                    border-bottom: 1px solid ${silverDefault};
                `}
            >
                <Icon
                    type={faBars}
                    css={css`
                        position: absolute;
                        left: 24px;

                        &[data-icon='bars'] {
                            width: 24px;
                            height: 20px;
                        }
                    `}
                    onClick={handleHamburgerClick}
                />
                <div data-target={getTargetAttribute(pageTitleDataTarget)}>
                    {currentPageTitle}
                    {headerContent}
                </div>
            </header>
        </>
    );
};

HamburgerNavigation.propTypes = {
    headerContent: PropTypes.node,
    selectedTab: PropTypes.oneOf(Object.values(NavigationTabs)),
};

export default HamburgerNavigation;
